<template>
  <div :class="$style.container">
    <div :class="$style.content">
      <router-view v-if="isReceivedDefaultLanguageTranslations && isCheckedAuthorization" />

      <div v-else :class="$style.loader">
        <CircleLoader />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { globalStore } from '@stores/globalStore'
import { localizationStore } from '@stores/localizationStore'

import { LanguageEnum } from '@models/entities/languages'

import { queryController } from '@composables/queryController'

import { isAvailableLocalStorage } from '@scripts/helpers/windowHelpers'
import { createLanguageTranslationsRequestUrl } from '@scripts/helpers/appHelpers'

import { LANGUAGE_MAP } from '@definitions/languages'

import CircleLoader from '@components/loaders/CircleLoader.vue'

const router = useRouter()
const globStore = globalStore()
const localStore = localizationStore()

const { getQueryByKey } = queryController()

const isCheckedAuthorization = computed(() => globStore.isCheckedAuthorization)

const isReceivedDefaultLanguageTranslations = computed(() => localStore.isReceivedDefaultLanguageTranslations)

const setLanguageToLocalStorage = (language: LanguageEnum | string) => {
  if (isAvailableLocalStorage()) {
    localStorage.setItem('lang', language)
  }
}

const getCurrentLanguageTranslations = async() => {
  const url = createLanguageTranslationsRequestUrl(globStore.getCurrentLanguage)
  await localStore.getDefaultLanguageTranslations(url)
}

const getAvailableLanguagesTranslations = () => {
  globStore.getAvailableLanguages
    .forEach((language: LanguageEnum) => {
      if (language !== globStore.getCurrentLanguage) {
        const url = createLanguageTranslationsRequestUrl(language)
        localStore.getAvailableLanguagesTranslations(url)
      }
    })
}

const setCurrentLanguage = async(isAuthorized: boolean) => {
  const languageList = Object.keys(LANGUAGE_MAP) as LanguageEnum[]
  const localStorageLanguage: LanguageEnum | string | null = isAvailableLocalStorage() ? localStorage.getItem('lang') : null

  if (isAuthorized) {
    if (
      globStore.getUserLanguage
      && languageList.some((languageKey: LanguageEnum) => languageKey === globStore.getUserLanguage)
    ) {
      globStore.setCurrentLanguage(globStore.getUserLanguage)
    } else if (
      localStorageLanguage
      && languageList.some((languageKey: LanguageEnum) => languageKey === localStorageLanguage)
    ) {
      globStore.setCurrentLanguage(localStorageLanguage as LanguageEnum)
      await globStore.postUserInfo({ lang: localStorageLanguage as LanguageEnum })
    } else {
      globStore.setCurrentLanguage(LanguageEnum.en)
      await globStore.postUserInfo({ lang: LanguageEnum.en })
    }
  } else {
    const queryLanguage: string[] | undefined = getQueryByKey<string>('lang')

    if (
      queryLanguage
      && queryLanguage.length
      && languageList.some((languageKey: LanguageEnum) => languageKey === queryLanguage[0])
    ) {
      globStore.setCurrentLanguage(queryLanguage[0] as LanguageEnum)
      setLanguageToLocalStorage(queryLanguage[0])
    } else if (
      localStorageLanguage
      && languageList.some((languageKey: LanguageEnum) => languageKey === localStorageLanguage)
    ) {
      globStore.setCurrentLanguage(localStorageLanguage as LanguageEnum)
    } else {
      globStore.setCurrentLanguage(LanguageEnum.en)
      setLanguageToLocalStorage(LanguageEnum.en)
    }
  }

  getCurrentLanguageTranslations()
}

watch(() => globStore.isAuthorized, (newVal) => {
  if (newVal) {
    setCurrentLanguage(true)
  }

  if (globStore.routeNameForAuthorizedStatusSwitching) {
    router.push({ name: globStore.routeNameForAuthorizedStatusSwitching })
  }
})

watch(() => globStore.isCheckedAuthorization, (newVal) => {
  if (newVal) {
    if (!globStore.isAuthorized) {
      setCurrentLanguage(false)
    }
  }
})

watch(() => isReceivedDefaultLanguageTranslations.value, async(newVal) => {
  if (newVal) {
    await globStore.getLanguages()
    setTimeout(() => {
      getAvailableLanguagesTranslations()
    }, 0)
  }
})
</script>

<style lang="scss" module>
.container {
  @include flex-column-center();
  width: 100%;
  height: 100dvh;
  padding: 20px 10px 0;
}

.content {
  @include size('100%');
}

.loader {
  @include flex-center-column();
  height: 100%;
}
</style>
